import { useEffect, useRef, useState, type RefObject } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useCase, useExtraction, type CaseRecordOut, type ExtractionStatus, type HandoverOut } from "@/api";
import { Button } from "@/components/Button";
import { CancelledNotice } from "@/components/CancelledNotice";
import { InCaseHead, useCaseHead } from "@/components/CaseHeadSlots";
import { CaseMenu } from "@/components/CaseMenu";
import { useChangeForm } from "@/components/ChangeFormDialog";
import { Figures } from "@/components/Figures";
import { ClockIcon } from "@/components/icons";
import type { MenuItem } from "@/components/Menu";
import { StateMessage } from "@/components/StateMessage";
import { CaseSendWarnings, useReadAhead } from "@/components/SendWarnings";
import { StageControl } from "@/components/stages/StageControl";
import { useStageChange } from "@/components/stages/useStageChange";
import { HandoverDialog } from "@/features/handover/HandoverDialog";
import { previousRequest } from "@/features/handover/previousRequest";
import { CANCEL_PARAM, HANDOVER_CLOSED_REASON, HANDOVER_PARAM } from "@/lib/caseDialogs";
import { FACADE_SENT, facadeEditability, RESTORE_PARAM } from "@/lib/facadeEditability";
import { activityTime, formatDay } from "@/lib/format";
import { canCancel, HANDOVER_CLOSED, stageOf } from "@/lib/stages";

import { CancelDialog } from "./CancelDialog";
import { RestoreDialog } from "./RestoreDialog";
import styles from "./Record.module.css";

/**
 * Where the next step is (design §8.2, rebased onto paket-a 27.09.2026): the
 * board or the calculation once a Stand exists; before that the worker's
 * reading — progress/failure on `/einlesen`, a finished one on `/schema`
 * where „Übernehmen" writes Stand 1 (A-014).
 *
 * With drawings on file and no reading yet (plan 4, Kauf-Test 12.10.2026):
 * „Zeichnung lesen →" to `/einlesen`, whose one button starts the reading —
 * the record itself still starts nothing (E-54: what leaves the house leaves
 * on a press there). Drawing by hand is the fallback (E-45), so it stays, but
 * as the quiet second way (`secondary`). R-4 is untouched: both lead to
 * Stand 1 of **this** Vorgang.
 *
 * Without a drawing, the outline by hand for this Vorgang (R-4, UX gate
 * round 1 #2): `/umriss?case=<id>`, whose „Übernehmen" appends Stand 1 here
 * instead of creating a new Vorgang. Not in a stage that is over: nobody
 * draws or reads the first Stand of a sent, decided or finished Vorgang.
 *
 * Not „„{Datei}“ als Zeichnung übernehmen" (round-3 screen check, R3-1):
 * taking a mail's drawing over files it under „Zeichnungen"; adopting is
 * filing, and lives in the entry's menu.
 */
interface NextStep {
  label: string;
  to: string;
  /** The quiet second way under the button — drawing by hand beside reading. */
  secondary?: { label: string; to: string };
}

function nextStep(record: CaseRecordOut, reading: ExtractionStatus | null): NextStep | null {
  const id = record.case.id;
  if (record.latestVersionNo === 0) {
    if (reading === "succeeded") return { label: "Zur Lesung →", to: `/vorgang/${id}/schema` };
    if (reading !== null) return { label: "Zur Auslese →", to: `/vorgang/${id}/einlesen` };
    const over = HANDOVER_CLOSED.has(record.case.status) || record.case.status === "sent";
    if (over) return null;
    const draw = `/umriss?case=${encodeURIComponent(id)}`;
    return record.documentsCount > 0
      ? { label: "Zeichnung lesen →", to: `/vorgang/${id}/einlesen`, secondary: { label: "Umriss selbst zeichnen", to: draw } }
      : { label: "Umriss zeichnen →", to: draw };
  }
  return stageOf(record.case.status).entry === "calculation"
    ? { label: "Zur Kalkulation →", to: `/vorgang/${id}/kalkulation` }
    : { label: "Zur Planung →", to: `/vorgang/${id}/planung` };
}

/**
 * Asked only with no Stand and a drawing on file (Ruling T7-1, as in
 * `CaseRoute`). `waiting` while that answer is out: the way on depends on
 * it, so the head holds its primary until then (final review of Plan D,
 * minor) — as the frame's tabs do — instead of offering „Umriss zeichnen →"
 * and swapping it for „Zur Lesung →" a moment later.
 */
function useReadingStatus(record: CaseRecordOut): { status: ExtractionStatus | null; waiting: boolean } {
  const unread = record.latestVersionNo === 0 && record.documentsCount > 0;
  const reading = useExtraction(unread ? record.case.id : null);
  return { status: reading.data?.status ?? null, waiting: unread && reading.isPending };
}

/**
 * Where the banner of a cancelled Vorgang stood, once it is back (checklist
 * 3.3, Soft-Go-Live 16.10.2026): who restored it and when — with the reason,
 * where one was given — and, in the banner's own words, the cancellation it
 * came back from. History, not a state: quiet ink, no box, nothing to press.
 */
function RestoredNote({ restoration }: { restoration: NonNullable<CaseRecordOut["restoration"]> }) {
  const { cancellation } = restoration;
  return (
    <div role="group" aria-label="Wiederhergestellt" className={styles.restored}>
      <p>
        <b>Wiederhergestellt</b> am <span className="tabular">{formatDay(restoration.at)}</span>
        {restoration.by !== null ? <> von {restoration.by}</> : null}
        {restoration.reason !== null ? <> — Grund: {restoration.reason}</> : null}
      </p>
      <p>
        Storniert am <span className="tabular">{formatDay(cancellation.at)}</span>
        {cancellation.by !== null ? <> von {cancellation.by}</> : null}
        {cancellation.reason !== null ? <> — Grund: {cancellation.reason}</> : null}
      </p>
    </div>
  );
}

/**
 * What a gesture on the record had to say, as the page says it (UX gate
 * round 1, #3): one notice pattern under the head — `success` for a gesture
 * that went through, `neutral` for news, `conflict` for a refusal.
 */
export interface RecordNotice {
  tone: "neutral" | "success" | "conflict";
  text: string;
}

interface RecordHeadProps {
  record: CaseRecordOut;
  /** The sentence a navigation brought („Du hast „…“ von … übernommen.", Task 13). */
  arrived?: string | null;
  /** The screen's one announcement — the head holds its live region. */
  announcement: string;
  onAnnounce: (sentence: string) => void;
  /** The last gesture's notice (a withdrawal, UX gate round 1 #3) — shown under the head until put away. */
  notice?: RecordNotice | null;
  onDismissNotice?: () => void;
  /** The record's one withdrawal is running — its one button is the waiting line's (UX gate round 3, #8). */
  withdrawing: boolean;
  onWithdraw: (open: HandoverOut) => void;
  /**
   * „Mehr …“, where „Übergeben …“ lives since Task 9 — where the focus goes
   * once a withdrawn request is gone (I2), and the opener of a dialog asked
   * for in the address.
   */
  moreRef: RefObject<HTMLButtonElement | null>;
}

export function RecordHead({
  record,
  arrived = null,
  announcement,
  onAnnounce,
  notice = null,
  onDismissNotice = () => undefined,
  withdrawing,
  onWithdraw,
  moreRef,
}: RecordHeadProps) {
  const navigate = useNavigate();
  const changes = useStageChange();
  // The Vorgang's `h1` is the frame's (Task 9, B-4): the record puts a
  // person down on it, and back on it where a gesture took its own opener.
  const heading = useCaseHead()?.heading ?? null;
  const waitingRef = useRef<HTMLDivElement>(null);
  // Set when a request went out (or turned out to be out already); read once
  // the record has been read again. Refs: nothing renders from them.
  const focusWaiting = useRef(false);
  const askedOn = useRef<CaseRecordOut | null>(null);
  // A 409 `already_pending` kept the dialog open (S-6): once it is closed,
  // the focus goes to the waiting line of the request that was running.
  const toWaitingOnClose = useRef(false);
  const [handingOver, setHandingOver] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [restoring, setRestoring] = useState(false);
  // A cancellation or a restoration takes the gesture that opened its dialog
  // with it („Vorgang stornieren …" is gone, „Wiederherstellen" is gone): the
  // dialog cannot hand the focus back, so the heading takes it.
  const toHeading = useRef(false);
  const restoreRef = useRef<HTMLButtonElement>(null);
  // The sentence a navigation brought stands until it is put away (#3).
  const [arrivedShown, setArrivedShown] = useState(true);
  const reading = useReadingStatus(record);
  const item = record.case;
  // „Angebot raus“ reads the Vorgang ahead on hover or focus (review final, 3).
  const readAhead = useReadAhead(item.id);
  const next = reading.waiting ? null : nextStep(record, reading.status);
  const pending = record.handover;
  const closed = HANDOVER_CLOSED.has(item.status);
  const held = closed || pending !== null;
  const cancelled = item.status === "cancelled";
  const cancellable = canCancel(item.status);
  // Who cancelled it and why live on the Vorgang, not on the record — asked
  // only while it is cancelled (the same query the documents tab reads).
  const cancellation = useCase(cancelled ? item.id : null).data?.cancellation ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const change = useChangeForm(item.id, {
    status: item.status,
    latestVersionNo: record.latestVersionNo,
    caseNo: item.caseNo,
    title: item.title,
  });

  // Opening a record puts a person on its heading (UX ruling 12, F-7) — once
  // the frame has drawn it, which is the same commit or the one after.
  const focusedOnOpen = useRef(false);
  useEffect(() => {
    if (focusedOnOpen.current || heading === null) return;
    focusedOnOpen.current = true;
    heading.focus();
  }, [heading]);

  // `?uebergeben=1` (UX gate round 2, #4): the start page's „Neu übergeben …"
  // after a declined or lapsed request leads here with the dialog asked for.
  // It opens on arrival — from „Mehr …", which holds the focus first so the
  // dialog hands it back there — and the parameter leaves the address,
  // so a reload or „Zurück" does not open it again. A Vorgang that cannot be
  // handed over now (finished, or a request already waiting) only loses the
  // parameter: „Mehr …" says why at the held „Übergeben …", and a waiting
  // request stands under the head.
  const wantsHandover = useRef(searchParams.get(HANDOVER_PARAM) === "1");
  useEffect(() => {
    if (!wantsHandover.current) return undefined;
    // From a timer, so no state is set synchronously in the effect.
    const timer = window.setTimeout(() => {
      wantsHandover.current = false;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(HANDOVER_PARAM);
          return next;
        },
        { replace: true },
      );
      if (held) return;
      moreRef.current?.focus();
      setHandingOver(true);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [held, moreRef, setSearchParams]);

  // `?stornieren=1` (spec 2026-10-15 §5), as `?uebergeben=1` above: the dialog
  // opens from „Mehr …", which holds the focus first, and the
  // parameter leaves the address. A Vorgang that cannot be cancelled (sent,
  // finished, already cancelled) only loses the parameter.
  const wantsCancel = useRef(searchParams.get(CANCEL_PARAM) === "1");
  useEffect(() => {
    if (!wantsCancel.current) return undefined;
    const timer = window.setTimeout(() => {
      wantsCancel.current = false;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(CANCEL_PARAM);
          return next;
        },
        { replace: true },
      );
      if (!cancellable) return;
      moreRef.current?.focus();
      setCancelling(true);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [cancellable, moreRef, setSearchParams]);

  // `?wiederherstellen=1` — the way back from the board and the calculation
  // of a cancelled Vorgang (`restorePath`), as `?stornieren=1` above: the
  // dialog opens from „Wiederherstellen“, which holds the focus first, and
  // the parameter leaves the address. A Vorgang that is not (any longer)
  // cancelled only loses the parameter.
  const wantsRestore = useRef(searchParams.get(RESTORE_PARAM) === "1");
  useEffect(() => {
    if (!wantsRestore.current) return undefined;
    const timer = window.setTimeout(() => {
      wantsRestore.current = false;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(RESTORE_PARAM);
          return next;
        },
        { replace: true },
      );
      if (!cancelled) return;
      restoreRef.current?.focus();
      setRestoring(true);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [cancelled, setSearchParams]);

  useEffect(() => {
    if (arrived === null) return undefined;
    const timer = window.setTimeout(() => onAnnounce(arrived), 50);
    return () => window.clearTimeout(timer);
  }, [arrived, onAnnounce]);

  // UX ruling 10: after „Übergabe anfragen" the focus goes to the waiting
  // line — as soon as the refetched record carries the request. A record
  // read again *without* it (the request raced, or was taken back in another
  // tab) ends the wait instead of leaving it armed for a later one (record
  // fix wave I2); a read that fails unmounts this head altogether.
  const pendingId = pending?.id ?? null;
  useEffect(() => {
    if (!focusWaiting.current || record === askedOn.current) return;
    focusWaiting.current = false;
    if (pendingId !== null) waitingRef.current?.focus();
  }, [record, pendingId]);

  /**
   * After a cancel or restore dialog: where the move took its own opener away
   * (see `toHeading`) — or somebody else's did, a 409 whose re-read record no
   * longer offers „Vorgang stornieren …" — the focus is on `<body>`, and the
   * heading takes it.
   */
  function backToHeading() {
    const lost = document.activeElement === null || document.activeElement === document.body;
    if (!toHeading.current && !lost) return;
    toHeading.current = false;
    heading?.focus();
  }

  function awaitRequest() {
    focusWaiting.current = true;
    askedOn.current = record;
    setHandingOver(false);
  }

  // „Mehr …“ (Task 9, ruling on B-8): the record's own gestures, in the head
  // beside the way on — „Form ändern …“, „Übergeben …“ and, apart and last as
  // the rare gesture with consequences (spec 2026-10-15 §5), „Vorgang
  // stornieren …“; a cancelled Vorgang is offered the way back alone. Each
  // opens its dialog here, from „Mehr …“, which takes the focus back.
  //
  // „Übergeben …“ stays while a request waits (UX gate round 1, #15) and in
  // a finished stage (D-4): held, with the waiting line or the reason as its
  // description — gone, it would leave the person guessing where it went.
  const menuItems: MenuItem[] = cancelled
    ? [{ key: "restore", label: "Wiederherstellen …", onSelect: () => setRestoring(true) }]
    : [
        ...(change.offered ? [{ key: "form", label: "Form ändern …", onSelect: change.ask }] : []),
        held
          ? pending !== null
            ? { key: "handover", label: "Übergeben …", disabled: true, describedBy: "handover-waiting" }
            : { key: "handover", label: "Übergeben …", disabled: true, hint: HANDOVER_CLOSED_REASON }
          : { key: "handover", label: "Übergeben …", onSelect: () => setHandingOver(true) },
        ...(cancellable
          ? [{ key: "cancel", label: "Vorgang stornieren …", onSelect: () => setCancelling(true), separatorBefore: true }]
          : []),
      ];
  // The one way on, primary, at the right end of the head — for a Vorgang
  // without a Stand the outline by hand, also when a mail holds a drawing
  // (R3-1, R3-9). A cancelled Vorgang has none: its way back is the banner's.
  const nextButton = cancelled ? null : reading.waiting ? (
    // Not yet a way: the button's place in its geometry, nothing to press or
    // to read out, as the frame's tabs wait (static, CI §6).
    <span data-testid="next-waiting" className={`${styles.boneButton} ${styles.nextSlot}`} aria-hidden="true" />
  ) : next !== null ? (
    <Button variant="primary" className={styles.nextButton} onClick={() => navigate(next.to)}>
      {next.label}
    </Button>
  ) : null;

  // One pattern for everything the record has to say (UX gate round 1, #3),
  // directly under the head, each with its ×: a refused stage step, the
  // outcome of a withdrawal, the sentence an accepted handover arrived with.
  // Not live regions of their own — the status line below announces the same
  // sentences; a region mounted with its text is not read out.
  const notices: { key: string; tone: RecordNotice["tone"]; text: string; dismiss: () => void }[] = [];
  if (changes.notice !== null) {
    notices.push({ key: "stage", tone: "conflict", text: changes.notice, dismiss: changes.clear });
  }
  if (notice !== null) notices.push({ key: "gesture", ...notice, dismiss: onDismissNotice });
  if (arrived !== null && arrivedShown) {
    notices.push({ key: "arrived", tone: "success", text: arrived, dismiss: () => setArrivedShown(false) });
  }
  const restored = !cancelled && record.restoration !== null;
  const below = notices.length > 0 || cancelled || restored || pending !== null;

  return (
    <>
      {/* Into the Vorgang's head (Task 9, B-1/B-4): the stage path with its
          gestures beside the tabs, and the way on with „Mehr …“ at the right
          of the name — the head is the frame's, the same on every tab; what
          only the record knows stands in its places. A cancelled Vorgang has
          no path: it would stand at „Neu“, the one point a finished stage
          counts as done, under a head that says „Storniert". Its one way,
          back, stands in the banner below (spec 2026-10-15 §5). */}
      {cancelled ? null : (
        <InCaseHead slot="path">
          <StageControl
            caseId={item.id}
            status={item.status}
            busy={changes.busy}
            onStage={(id, to) => void changes.stage(id, to)}
            // UX-Gutachten 4: the same question as the calculation's,
            // with what the mail asked for and the offer does not hold.
            sendWarnings={(id, onShown) => <CaseSendWarnings caseId={item.id} id={id} onShown={onShown} />}
            onSendIntent={readAhead}
          />
        </InCaseHead>
      )}
      <InCaseHead slot="actions">
        <div className={styles.headActions}>
          <div className={styles.headButtons}>
            <CaseMenu items={menuItems} triggerRef={moreRef} />
            {nextButton}
          </div>
          {/* The quiet second way under the button — drawing by hand beside
              reading (R3-1, R3-9). And where „Vorgang stornieren …" and
              „Form ändern …" stood until the offer went out, why they are
              gone (spec 2026-10-15 §4, V-5): gone without a word, the head
              looked as if they had never existed. */}
          {next?.secondary ? (
            <p className={styles.secondaryWay}>
              <Link className="inline-link" to={next.secondary.to}>
                {next.secondary.label}
              </Link>
            </p>
          ) : facadeEditability(item.status) === "sent" ? (
            <p className={styles.handoverReason}>{FACADE_SENT}</p>
          ) : null}
        </div>
      </InCaseHead>
      <p role="status" className="visually-hidden">
        {changes.notice ?? announcement}
      </p>
      {below ? (
        <div className={styles.headStack}>
          {notices.length > 0 ? (
            <div className={styles.notices}>
              {notices.map((each) => (
                <StateMessage
                  key={each.key}
                  tone={each.tone}
                  title={each.text}
                  live={false}
                  onDismiss={() => {
                    each.dismiss();
                    // The × is gone with its notice: the page's heading takes the focus.
                    heading?.focus();
                  }}
                />
              ))}
            </div>
          ) : null}
          {cancelled ? (
            // The state of a cancelled Vorgang where the eye is, a state of its
            // own under the head like the waiting line below (spec 2026-10-15
            // §5): when, by whom, why — and the one way back. The board and the
            // calculation show the same notice, with the way here instead.
            <CancelledNotice
              cancellation={cancellation}
              action={
                <Button
                  ref={restoreRef}
                  variant="primary"
                  aria-describedby="case-cancelled"
                  onClick={() => setRestoring(true)}
                >
                  Wiederherstellen
                </Button>
              }
            />
          ) : null}
          {restored && record.restoration !== null ? <RestoredNote restoration={record.restoration} /> : null}
          {pending !== null ? (
            // The waiting state where the eye is (UX ruling 10, F-8), as a state
            // of its own under the head (UX gate round 1, #15). Who, since
            // when, until which day — and the way back. It is also the reason
            // „Übergeben …" is held in „Mehr …".
            <div ref={waitingRef} tabIndex={-1} role="group" aria-label="Offene Übergabe" className={styles.waiting}>
              <ClockIcon className={styles.waitingIcon} />
              <p id="handover-waiting" className={styles.waitingText}>
                Übergabe wartet auf <b>{pending.toDisplayName}</b> · angefragt{" "}
                <Figures text={activityTime(pending.requestedAt)} /> ·
                verfällt am <span className="tabular">{formatDay(pending.expiresAt)}</span>
              </p>
              <Button
                variant="quiet"
                flush
                className={styles.waitingAction}
                aria-disabled={withdrawing ? "true" : undefined}
                onClick={() => onWithdraw(pending)}
              >
                {withdrawing ? "Wird zurückgezogen …" : "Anfrage zurückziehen"}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {change.dialog}
      {handingOver ? (
        <HandoverDialog
          caseId={item.id}
          caseTitle={item.title}
          caseNo={item.caseNo}
          pendingRecipient={pending?.toDisplayName ?? null}
          // How the last request ended, from the record's timeline (R3-12):
          // „Neu übergeben …" after a refusal shows who declined and why.
          previous={previousRequest(record.timeline)}
          onClose={() => setHandingOver(false)}
          // After the dialog's own cleanup, which handed the focus back to
          // „Mehr …" — „Übergeben …" in it is held by now, the record shows
          // the request.
          onClosed={() => {
            if (!toWaitingOnClose.current) return;
            toWaitingOnClose.current = false;
            if (waitingRef.current !== null) {
              waitingRef.current.focus();
              return;
            }
            // The record has not come back with the request yet: wait for it.
            focusWaiting.current = true;
            askedOn.current = record;
          }}
          onRequested={(handover) => {
            awaitRequest();
            onAnnounce(`Übergabe an ${handover.toDisplayName} angefragt.`);
          }}
          onAlreadyPending={() => {
            toWaitingOnClose.current = true;
          }}
          // „Abbrechen" after the 409 (R2-8): back to „Mehr …", as after any
          // „Abbrechen" — not to the waiting line.
          onCancelRunning={() => {
            toWaitingOnClose.current = false;
          }}
          // The record, read again, answers 404 and the screen says
          // „nicht gefunden" — the dialog has nothing left to offer.
          onGone={() => setHandingOver(false)}
        />
      ) : null}
      {cancelling ? (
        <CancelDialog
          caseId={item.id}
          caseNo={item.caseNo}
          title={item.title}
          pendingRecipient={pending?.toDisplayName ?? null}
          released={item.status === "quote_approved"}
          onClose={() => setCancelling(false)}
          onDone={(sentence) => {
            toHeading.current = true;
            onAnnounce(sentence);
            setCancelling(false);
          }}
          onClosed={backToHeading}
        />
      ) : null}
      {restoring ? (
        <RestoreDialog
          caseId={item.id}
          caseNo={item.caseNo}
          title={item.title}
          onClose={() => setRestoring(false)}
          onDone={(sentence) => {
            toHeading.current = true;
            onAnnounce(sentence);
            setRestoring(false);
          }}
          onClosed={backToHeading}
        />
      ) : null}
    </>
  );
}
